import * as React from 'react';
import { injectIntl, WrappedComponentProps as InjectedIntlProps } from 'react-intl';
import { Button, Checkbox } from 'semantic-ui-react';

import { ErrorBoundary } from '../../containers/ErrorBoundary';
import ErrorCatch from '../../containers/ErrorBoundary/ErrorCatch';
import { localeData } from '../../locales/data';

import './SelectColumns.scss';
import { FormattedMessageHelper } from '../../shared/helpers/internationalization';

type ColumnType = {
  id: string;
  name: string;
  parent?: string;
  category?: string;
  active: boolean;
  display: boolean;
};

export interface SelectColumnsProps {
  categories?: string[];
  options: ColumnType[];
  handleCheckBox: (e: any, data: object) => void;
  handleApply: (e: any) => void;
  handleReset: (e: any) => void;
  handleClosePopup: () => void;
}

type SelectColumnsPropsGeneric = SelectColumnsProps & InjectedIntlProps;

const checkboxes = (options: any[], handleCheckBox: (e: any, data: object) => void) =>
  options.map((column: ColumnType, index: number) => {
    if (column.display) {
      let value = column.id;
      if (column.parent) {
        value = `${value}_${column.parent}`;
      }

      return <Checkbox key={index} onClick={handleCheckBox} checked={column.active} label={column.name} value={value} />;
    } else {
      return null;
    }
  });

const withCategories = (categories: string[], options: ColumnType[], handleCheckBox: (e: any, data: object) => void) =>
  categories.map(category => (
    <div className={`selectColumns__options-${category}`} key={category}>
      <div className="selectColumns__options-title">{category}</div>
      {checkboxes(
        options.filter(column => column.category === category),
        handleCheckBox
      )}
    </div>
  ));

const withoutCategories = (options: ColumnType[], handleCheckBox: (e: any, data: object) => void) => (
  <div className={'selectColumns__options-columns'}>
    <div className="selectColumns__options-title">
      <FormattedMessageHelper
        tagName="span"
        id="selectColumns.wihtoutCategories.1"
        defaultMessage={localeData.en['selectColumns.wihtoutCategories.1']}
      />
    </div>
    {checkboxes(options, handleCheckBox)}
  </div>
);

const SelectColumns = ({
  categories,
  options,
  handleCheckBox,
  handleApply,
  handleReset,
  handleClosePopup,
  intl,
}: SelectColumnsPropsGeneric): JSX.Element => {
  try {
    const handleApplyLocal = (event: any) => {
      handleApply(event);
    };

    return (
      <ErrorBoundary showUI={true} isNotFullScreen={true} isModal={true}>
        <div className="selectColumns">
          <div className="selectColumns__header">
            <div className="selectColumns__header-title">
              <FormattedMessageHelper tagName="span" id="selectColumns.1" defaultMessage={localeData.en['selectColumns.1']} />
            </div>
            <div onClick={handleClosePopup} className="selectColumns__header-close">
              {' '}
              &times;{' '}
            </div>
          </div>
          <div className="selectColumns__options">
            {categories ? withCategories(categories, options, handleCheckBox) : withoutCategories(options, handleCheckBox)}
          </div>
          <div className="selectColumns__buttons">
            <Button onClick={handleReset} className="button button--secondary">
              {intl.formatMessage({ id: 'selectColumns.2', defaultMessage: localeData.en['selectColumns.2'] })}
            </Button>
            <Button onClick={handleApplyLocal} className="button button--primary">
              {intl.formatMessage({ id: 'selectColumns.3', defaultMessage: localeData.en['selectColumns.3'] })}
            </Button>
          </div>
        </div>
      </ErrorBoundary>
    );
  } catch (error) {
    return <ErrorCatch error={error} />;
  }
};

export default injectIntl(SelectColumns);
